import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { diffWords } from 'diff'
import { mdiAlertOutline } from '@mdi/js'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/checkbox'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { AppIcon } from '../ui/icon'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { notify } from '../../lib/notifications'
import { SECTION_ICONS } from '../../lib/section-icons'
import {
    KNOWLEDGE_KINDS,
    ProposalStaleError,
    useApproveKnowledgeProposal,
    useCreateKnowledgeProposal,
    useDecideKnowledgeProposal,
    type CreateKnowledgeProposalRequest,
    type KnowledgeKind,
    type KnowledgeProposal,
} from '../../hooks/useKnowledge'
import { KindBadge } from './KindBadge'
import { AssetPicker, isAssetPickerEscapeToClear } from '../AssetPicker'

/** One place a new note can live. */
export type ProposalTarget = { type: 'asset' | 'organization'; uid?: string; label: string }

/** What the compose dialog opens on: a draft, a correction, or nothing. */
export type ProposalSeed = {
    action: 'create' | 'update' | 'retire'
    page_uid?: string
    target_entity_type?: 'asset' | 'organization'
    target_entity_uid?: string
    kind?: KnowledgeKind
    title?: string
    content?: string
    applies_to_subtree?: boolean
    rationale?: string
    conversation_uid?: string
    message_uid?: string
    fact_id?: string
    current_text?: string
    current_title?: string
}

/** A word diff of two texts: removed words struck through, added ones marked. */
export function WordDiff({ before, after }: { before: string; after: string }) {
    const parts = useMemo(() => diffWords(before, after), [before, after])

    return (
        <p className="whitespace-pre-wrap text-sm" data-testid="knowledge-proposal-diff">
            {parts.map((part, i) =>
                part.added ? (
                    <ins key={i} className="bg-success/15 text-foreground no-underline" data-testid="diff-added">
                        {part.value}
                    </ins>
                ) : part.removed ? (
                    <del key={i} className="bg-destructive/15 text-muted-foreground" data-testid="diff-removed">
                        {part.value}
                    </del>
                ) : (
                    <span key={i}>{part.value}</span>
                ),
            )}
        </p>
    )
}

function targetKey(type?: string, uid?: string) {
    return type === 'organization' ? 'organization' : `asset:${uid ?? ''}`
}

/**
 * Writes a knowledge proposal (spec 2026-11-01-01): *Save as knowledge* on a
 * reply, *This note is wrong* on the triage card. *Save* files it with
 * `apply: true`, so a `knowledge.write` holder on the target gets the note at
 * once and anyone else a pending proposal. Personal data the draft still
 * carries is a warning, never a block.
 */
export function KnowledgeProposalDialog({
    org,
    open,
    onOpenChange,
    seed,
    targets,
    personalDataHits,
    loading,
    notice,
}: {
    org: string
    open: boolean
    onOpenChange: (open: boolean) => void
    seed: ProposalSeed | null
    targets: ProposalTarget[]
    personalDataHits?: string[]
    /** The draft job is still running. */
    loading?: boolean
    /** Why the dialog opened empty (a failed or capped draft). */
    notice?: string | null
}) {
    const { t } = useTranslation('knowledge')
    const create = useCreateKnowledgeProposal(org)
    const [target, setTarget] = useState('')
    const [kind, setKind] = useState<KnowledgeKind>('note')
    const [title, setTitle] = useState('')
    const [content, setContent] = useState('')
    const [subtree, setSubtree] = useState(true)
    const [error, setError] = useState<string | null>(null)
    // Any other asset, picked by name.
    const [picked, setPicked] = useState<ProposalTarget | null>(null)

    useEffect(() => {
        if (!seed) return
        setPicked(null)
        setTarget(seed.target_entity_type ? targetKey(seed.target_entity_type, seed.target_entity_uid) : targetKey(targets[0]?.type, targets[0]?.uid))
        setKind(seed.kind ?? 'note')
        setTitle(seed.title ?? '')
        setContent(seed.content ?? '')
        setSubtree(seed.applies_to_subtree ?? true)
        setError(null)
        // A new seed is a new dialog; the targets follow it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seed])

    const isUpdate = seed?.action === 'update'
    const options = useMemo(() => {
        const out = [...targets]
        if (picked && !out.some((o) => o.uid === picked.uid)) out.push(picked)
        if (seed?.target_entity_type && !out.some((o) => targetKey(o.type, o.uid) === targetKey(seed.target_entity_type, seed.target_entity_uid))) {
            out.unshift({ type: seed.target_entity_type, uid: seed.target_entity_uid, label: seed.target_entity_type === 'organization' ? t('proposals.target_organization') : '' })
        }

        return out
    }, [targets, seed, t, picked])
    const chosen = options.find((o) => targetKey(o.type, o.uid) === target)

    const save = async () => {
        if (!seed) return
        if (!title.trim() || !content.trim()) {
            setError(t('proposals.title_required'))

            return
        }
        const body: CreateKnowledgeProposalRequest = {
            action: seed.action,
            page_uid: seed.page_uid,
            kind,
            title: title.trim(),
            content: content.trim(),
            rationale: seed.rationale,
            conversation_uid: seed.conversation_uid,
            message_uid: seed.message_uid,
            fact_id: seed.fact_id,
            apply: true,
        }
        if (!isUpdate && chosen) {
            body.target_entity_type = chosen.type
            body.target_entity_uid = chosen.type === 'asset' ? chosen.uid : undefined
            body.applies_to_subtree = chosen.type === 'asset' ? subtree : undefined
        }
        try {
            const res = await create.mutateAsync(body)
            notify({
                key: 'knowledge_proposal',
                variant: 'default',
                titleKey: res.applied ? 'knowledge:proposals.saved_applied' : 'knowledge:proposals.saved_pending',
            })
            onOpenChange(false)
        } catch (e) {
            setError(e instanceof Error ? e.message : t('proposals.save_failed'))
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
                onEscapeKeyDown={(e) => {
                    if (isAssetPickerEscapeToClear(e)) e.preventDefault()
                }}
                data-testid="knowledge-proposal-dialog"
            >
                <DialogHeader>
                    <DialogTitle>{isUpdate ? t('proposals.dialog_title_correction') : t('proposals.dialog_title_new')}</DialogTitle>
                    <DialogDescription>{t('proposals.dialog_description')}</DialogDescription>
                </DialogHeader>
                {loading ? (
                    <p className="text-sm text-muted-foreground" data-testid="knowledge-proposal-drafting">
                        {t('proposals.drafting')}
                    </p>
                ) : (
                    <div className="space-y-3">
                        {notice && (
                            <p className="text-sm text-muted-foreground" role="status" data-testid="knowledge-proposal-notice">
                                {notice}
                            </p>
                        )}
                        {personalDataHits && personalDataHits.length > 0 && (
                            <p
                                className="flex items-start gap-1.5 rounded-md border border-warning/40 bg-warning/10 p-2 text-sm text-foreground"
                                role="alert"
                                data-testid="knowledge-proposal-pii-warning"
                            >
                                <AppIcon path={mdiAlertOutline} size="sm" className="mt-0.5 shrink-0" aria-hidden />
                                {t('proposals.pii_warning', { hits: personalDataHits.join(', ') })}
                            </p>
                        )}
                        {seed?.rationale && (
                            <p className="text-xs text-muted-foreground" data-testid="knowledge-proposal-rationale">
                                {t('proposals.rationale', { text: seed.rationale })}
                            </p>
                        )}
                        {!isUpdate && options.length > 0 && (
                            <div className="space-y-1">
                                <Label>{t('proposals.target_label')}</Label>
                                <Select value={target} onValueChange={setTarget}>
                                    <SelectTrigger data-testid="knowledge-proposal-target">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {options.map((o) => (
                                            <SelectItem
                                                key={targetKey(o.type, o.uid)}
                                                value={targetKey(o.type, o.uid)}
                                                data-testid={`knowledge-proposal-target-${o.type === 'organization' ? 'organization' : o.uid}`}
                                            >
                                                {o.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <AssetPicker
                                    org={org}
                                    value={picked?.uid}
                                    valueLabel={picked?.label}
                                    onChange={(uid, name, pathLabel) => {
                                        if (!uid) return
                                        setPicked({ type: 'asset', uid, label: pathLabel || name })
                                        setTarget(targetKey('asset', uid))
                                    }}
                                    testId="knowledge-proposal-asset-picker"
                                />
                            </div>
                        )}
                        <div className="flex flex-wrap items-end gap-4">
                            <div className="space-y-1">
                                <Label>{t('kind_label')}</Label>
                                <Select value={kind} onValueChange={(v) => setKind(v as KnowledgeKind)}>
                                    <SelectTrigger className="w-44" data-testid="knowledge-proposal-kind">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {KNOWLEDGE_KINDS.map((k) => (
                                            <SelectItem key={k} value={k} data-testid={`knowledge-proposal-kind-${k}`}>
                                                {t(`kind.${k}`)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            {!isUpdate && chosen?.type === 'asset' && (
                                <label className="flex items-center gap-2 pb-2 text-sm" htmlFor="knowledge-proposal-subtree">
                                    <Checkbox
                                        id="knowledge-proposal-subtree"
                                        checked={subtree}
                                        onCheckedChange={setSubtree}
                                        data-testid="knowledge-proposal-subtree"
                                    />
                                    {t('applies_to_subtree')}
                                </label>
                            )}
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="knowledge-proposal-title">{t('title_label')}</Label>
                            <Input
                                id="knowledge-proposal-title"
                                value={title}
                                maxLength={200}
                                onChange={(e) => setTitle(e.target.value)}
                                data-testid="knowledge-proposal-title"
                            />
                        </div>
                        <div className={isUpdate && seed?.current_text ? 'grid gap-3 md:grid-cols-2' : ''}>
                            {isUpdate && seed?.current_text && (
                                <div className="space-y-1" data-testid="knowledge-proposal-current">
                                    <Label>{t('proposals.current_note')}</Label>
                                    <p className="whitespace-pre-wrap rounded-md border bg-muted/40 p-2 text-sm">{seed.current_text}</p>
                                </div>
                            )}
                            <div className="space-y-1">
                                <Label htmlFor="knowledge-proposal-content">{t('proposals.content_label')}</Label>
                                <Textarea
                                    id="knowledge-proposal-content"
                                    rows={6}
                                    maxLength={8000}
                                    value={content}
                                    placeholder={t('proposals.content_placeholder')}
                                    onChange={(e) => setContent(e.target.value)}
                                    data-testid="knowledge-proposal-content"
                                />
                            </div>
                        </div>
                        {error && (
                            <p className="text-sm text-destructive" role="alert" data-testid="knowledge-proposal-error">
                                {error}
                            </p>
                        )}
                    </div>
                )}
                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="knowledge-proposal-cancel">
                        {t('cancel')}
                    </Button>
                    <Button onClick={() => void save()} disabled={loading || create.isPending || !seed} data-testid="knowledge-proposal-save">
                        {t('proposals.save')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

/**
 * A proposal in review mode (spec 2026-11-01-01): the word diff of an update,
 * the three texts once the note moved, *Approve* (after an optional edit) and
 * *Reject* with a reason for a decider, *Withdraw* for the proposer.
 */
/**
 * The note an approved proposal wrote, highlighted where it lives. An asset
 * note is not listed on /knowledge, so without it the approval looks lost.
 */
export function ProposalNoteLink({ org, proposal }: { org: string; proposal: KnowledgeProposal }) {
    const { t } = useTranslation('knowledge')
    const noteUid = proposal.result_page_uid
    if (proposal.status !== 'approved' || proposal.action === 'retire' || !noteUid) return null
    const className = 'inline-flex items-center gap-1 text-sm text-primary hover:underline'
    const content = (
        <>
            <AppIcon path={SECTION_ICONS.knowledge} size="xs" aria-hidden />
            {t('proposals.view_note')}
        </>
    )

    return proposal.target.type === 'asset' && proposal.target.asset_uid ? (
        <Link
            to="/$org/assets/$assetUid"
            params={{ org, assetUid: proposal.target.asset_uid }}
            search={{ tab: 'activity', note: noteUid }}
            className={className}
            data-testid="knowledge-proposal-note-link"
        >
            {content}
        </Link>
    ) : (
        <Link to="/$org/knowledge" params={{ org }} search={{ note: noteUid }} className={className} data-testid="knowledge-proposal-note-link">
            {content}
        </Link>
    )
}

export function KnowledgeProposalReviewDialog({
    org,
    proposal,
    onClose,
}: {
    org: string
    proposal: KnowledgeProposal | null
    onClose: () => void
}) {
    const { t } = useTranslation('knowledge')
    const approve = useApproveKnowledgeProposal(org)
    const reject = useDecideKnowledgeProposal(org, 'reject')
    const withdraw = useDecideKnowledgeProposal(org, 'withdraw')
    const [title, setTitle] = useState('')
    const [content, setContent] = useState('')
    const [stale, setStale] = useState<KnowledgeProposal | null>(null)
    const [rejecting, setRejecting] = useState(false)
    const [reason, setReason] = useState('')
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        setTitle(proposal?.title ?? '')
        setContent(proposal?.content ?? '')
        setStale(proposal?.status === 'stale' ? proposal : null)
        setRejecting(false)
        setReason('')
        setError(null)
    }, [proposal])

    if (!proposal) return null
    const open = proposal.status === 'pending' || proposal.status === 'stale'
    const decidable = proposal.can_decide && open
    const shown = stale ?? proposal
    const edits =
        title !== proposal.title || content !== proposal.content
            ? { title: title !== proposal.title ? title : undefined, content: content !== proposal.content ? content : undefined }
            : undefined

    const doApprove = async (overrideStale: boolean) => {
        setError(null)
        try {
            await approve.mutateAsync({ uid: proposal.uid, overrideStale, edits })
            notify({ key: 'knowledge_proposal', variant: 'default', titleKey: 'knowledge:proposals.approved' })
            onClose()
        } catch (e) {
            if (e instanceof ProposalStaleError) {
                setStale(e.proposal ?? { ...proposal, status: 'stale' })

                return
            }
            setError(e instanceof Error ? e.message : t('proposals.save_failed'))
        }
    }
    const doReject = async () => {
        if (!reason.trim()) return
        try {
            await reject.mutateAsync({ uid: proposal.uid, reason: reason.trim() })
            onClose()
        } catch (e) {
            setError(e instanceof Error ? e.message : t('proposals.save_failed'))
        }
    }

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="knowledge-proposal-dialog" data-mode="review">
                <DialogHeader>
                    <DialogTitle>{t('proposals.dialog_title_review')}</DialogTitle>
                    <DialogDescription className="flex flex-wrap items-center gap-2">
                        <span>{t(`proposals.action.${proposal.action}`)}</span>
                        <KindBadge kind={proposal.kind} />
                        <span>
                            {proposal.target.type === 'organization'
                                ? t('proposals.target_organization')
                                : t('proposals.target_asset', { name: proposal.target.asset_name ?? '' })}
                        </span>
                        <span>· {t(`proposals.origin.${proposal.origin}`)}</span>
                        {proposal.proposed_by_name && <span>· {t('proposals.by', { name: proposal.proposed_by_name })}</span>}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    {proposal.rationale && (
                        <p className="text-xs text-muted-foreground" data-testid="knowledge-proposal-rationale">
                            {t('proposals.rationale', { text: proposal.rationale })}
                        </p>
                    )}
                    {proposal.decision_reason && (
                        <p className="text-sm text-muted-foreground" data-testid="knowledge-proposal-reason">
                            {t('proposals.rejected_reason', { reason: proposal.decision_reason })}
                        </p>
                    )}
                    {stale && (
                        <div className="space-y-2 rounded-md border border-warning/40 bg-warning/10 p-2" data-testid="knowledge-proposal-stale">
                            <p className="text-sm">{t('proposals.stale_warning')}</p>
                            <div className="grid gap-2 md:grid-cols-3">
                                <div>
                                    <div className="text-xs text-muted-foreground">{t('proposals.stale_seen')}</div>
                                    <p className="whitespace-pre-wrap text-sm">{shown.seen_text}</p>
                                </div>
                                <div>
                                    <div className="text-xs text-muted-foreground">{t('proposals.stale_now')}</div>
                                    <p className="whitespace-pre-wrap text-sm">{shown.current_text}</p>
                                </div>
                                <div>
                                    <div className="text-xs text-muted-foreground">{t('proposals.proposed_note')}</div>
                                    <p className="whitespace-pre-wrap text-sm">{content}</p>
                                </div>
                            </div>
                        </div>
                    )}
                    {proposal.action === 'retire' ? (
                        <div className="space-y-1">
                            <p className="text-sm font-medium">{proposal.current_title ?? proposal.title}</p>
                            <p className="whitespace-pre-wrap text-sm text-muted-foreground line-through">{proposal.current_text}</p>
                            <p className="text-sm">{t('proposals.retire_body')}</p>
                        </div>
                    ) : (
                        <>
                            {proposal.action === 'update' && !stale && proposal.current_text != null && (
                                <div className="space-y-1">
                                    <div className="text-xs text-muted-foreground">{t('proposals.diff_title')}</div>
                                    <div className="rounded-md border bg-muted/40 p-2">
                                        <WordDiff before={proposal.current_text} after={content} />
                                    </div>
                                </div>
                            )}
                            {decidable ? (
                                <>
                                    <div className="space-y-1">
                                        <Label htmlFor="knowledge-review-title">{t('title_label')}</Label>
                                        <Input
                                            id="knowledge-review-title"
                                            value={title}
                                            maxLength={200}
                                            onChange={(e) => setTitle(e.target.value)}
                                            data-testid="knowledge-proposal-title"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <Label htmlFor="knowledge-review-content">{t('proposals.content_label')}</Label>
                                        <Textarea
                                            id="knowledge-review-content"
                                            rows={6}
                                            maxLength={8000}
                                            value={content}
                                            onChange={(e) => setContent(e.target.value)}
                                            data-testid="knowledge-proposal-content"
                                        />
                                    </div>
                                </>
                            ) : (
                                <div className="space-y-1">
                                    <p className="text-sm font-medium">{proposal.title}</p>
                                    <p className="whitespace-pre-wrap text-sm" data-testid="knowledge-proposal-text">
                                        {proposal.content}
                                    </p>
                                    <p className="text-xs text-muted-foreground">{t(`proposals.status.${proposal.status}`)}</p>
                                    <ProposalNoteLink org={org} proposal={proposal} />
                                </div>
                            )}
                        </>
                    )}
                    {rejecting && (
                        <div className="space-y-1">
                            <Label htmlFor="knowledge-reject-reason">{t('proposals.reject_reason')}</Label>
                            <Input
                                id="knowledge-reject-reason"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                data-testid="knowledge-proposal-reject-reason"
                            />
                        </div>
                    )}
                    {error && (
                        <p className="text-sm text-destructive" role="alert" data-testid="knowledge-proposal-error">
                            {error}
                        </p>
                    )}
                </div>
                <DialogFooter className="flex-wrap gap-2">
                    <Button variant="ghost" onClick={onClose} data-testid="knowledge-proposal-close">
                        {t('proposals.close')}
                    </Button>
                    {proposal.mine && open && !decidable && (
                        <Button
                            variant="outline"
                            disabled={withdraw.isPending}
                            onClick={() => void withdraw.mutateAsync({ uid: proposal.uid }).then(onClose)}
                            data-testid="knowledge-proposal-withdraw"
                        >
                            {t('proposals.withdraw')}
                        </Button>
                    )}
                    {decidable &&
                        (rejecting ? (
                            <Button
                                variant="destructive"
                                disabled={!reason.trim() || reject.isPending}
                                onClick={() => void doReject()}
                                data-testid="knowledge-proposal-reject-confirm"
                            >
                                {t('proposals.reject_confirm')}
                            </Button>
                        ) : (
                            <Button variant="outline" onClick={() => setRejecting(true)} data-testid="knowledge-proposal-reject">
                                {t('proposals.reject')}
                            </Button>
                        ))}
                    {decidable && (
                        <Button disabled={approve.isPending} onClick={() => void doApprove(!!stale)} data-testid="knowledge-proposal-approve">
                            {stale ? t('proposals.approve_anyway') : t('proposals.approve')}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
